import axios from "axios";
import { apiUrl, getAuthHeader } from "./base";
import { DEFAULT_SELECT_OPTION } from "../../config/constants";

export type TOptionItem = {
  label: string;
  value: any;
};

export type TWLFormatPagingSearchParam = {
  name?: string;
  page?: number;
  perPage?: number;
  isArchived?: boolean;
};

export type TWLFormatCreateParam = {
  name: string;
  description?: string;
  fields: Array<string>;
};

export type TWLFormatEditParam = {
  name: string;
  description?: string;
  fields: Array<string>;
  modifiedUserId?: string;
};

const pagingSearch = async (param: TWLFormatPagingSearchParam) => {
  const ret = {
    data: [],
    total: 0,
  };

  try {
    const { name, isArchived, page = 1, perPage = 10 } = param;
    let targetUrl = `/apiwlformat?page=${page}&perPage=${perPage}`;
    if (name && name.trim()) {
      targetUrl += `&name=${name.trim()}`;
    }
    if (typeof isArchived === "boolean") {
      targetUrl += `&isArchived=${isArchived}`;
    }

    const req = await axios.get(apiUrl(targetUrl), await getAuthHeader());
    const res = req.data;

    if (
      !(
        res.result === "OK" &&
        Array.isArray(res.data) &&
        typeof res.totalCount === "number"
      )
    ) {
      throw new Error();
    }

    ret.data = res.data;
    ret.total = res.totalCount;
  } catch (ignore) {}

  return ret;
};

const getOptions = async (programmeId: string) => {
  let options: TOptionItem[] = [
    // Since it's mandatory, disallow unselect the dropdown
    // { ...DEFAULT_SELECT_OPTION, value: null }
  ];
  try {
    const req = await axios.get(
      apiUrl(`/apiwlformat/options/${programmeId}`),
      await getAuthHeader()
    );
    const responseData = req.data;
    if (req.status === 200 && Array.isArray(responseData.data)) {
      options = options.concat(responseData.data);
    }
  } catch (ignore) {}
  return options;
};

const getById = async (id: string) => {
  try {
    const req = await axios.get(
      apiUrl(`/apiwlformat/${id}`),
      await getAuthHeader()
    );
    return req.data;
  } catch (ignore) {
    throw ignore;
  }
};

const create = async (param: TWLFormatCreateParam) => {
  try {
    const req = await axios.post(
      apiUrl(`/apiwlformat`),
      param,
      await getAuthHeader()
    );
    return req.data;
  } catch (ignore) {
    throw ignore;
  }
};

const update = async (id: string, param: TWLFormatEditParam) => {
  try {
    const req = await axios.patch(
      apiUrl(`/apiwlformat/${id}`),
      param,
      await getAuthHeader()
    );
    return req.data;
  } catch (ignore) {
    throw ignore;
  }
};

const updateState = async (id: string, isArchived: boolean) => {
  try {
    const req = await axios.patch(
      apiUrl(`/apiwlformat/state/${id}`),
      { isArchived },
      await getAuthHeader()
    );
    return req.data;
  } catch (ignore) {
    throw ignore;
  }
};

const duplicate = async (id: string, name?: string) => {
  try {
    const req = await axios.post(
      apiUrl(`/apiwlformat/duplicate/${id}`),
      { name },
      await getAuthHeader()
    );
    return req.data;
  } catch (ignore) {
    throw ignore;
  }
};

const deleteById = async (id: string) => {
  try {
    const req = await axios.delete(
      apiUrl(`/apiwlformat/${id}`),
      await getAuthHeader()
    );
    return req.data;
  } catch (ignore) {
    throw ignore;
  }
};

const deleteByIds = async (ids: Array<string>) => {
  try {
    const req = await axios.delete(apiUrl(`/apiwlformat/bulk`), {
      ...(await getAuthHeader()),
      data: ids,
    });
    return req.data;
  } catch (ignore) {
    throw ignore;
  }
};

const ApiWLFormat = {
  pagingSearch,
  getOptions,
  getById,
  create,
  update,
  updateState,
  duplicate,
  deleteById,
  deleteByIds,
};

export default ApiWLFormat;
